import { useIsCopilotEnabled } from '@common/Copilot/useIsCopilotEnabled';
import { StyledDowngradedToStarterBanner } from '@common/credits/ProfessionalEdition/DowngradedToStarterBanner';
import { useIsImportBlockedByPlan } from '@common/credits/ProfessionalEdition/hooks';
import { ProfessionalEditionCreditsProvider } from '@common/credits/ProfessionalEdition/ProfessionalEditionCreditsContext';
import { SnowflakePwdAuthBanner } from '@common/Fabric/SnowflakePwdAuthBanner';
import { PageHeader } from '@common/Header/PageHeader';
import { useHasOnlyAnalystFabric, useOnboardingDetail } from '@common/onboarding/hooks';
import { getSparkOnboardingFlow, getSqlOnboardingFlow } from '@common/onboarding/ide-utils';
import { OnboardingStatus } from '@common/onboarding/types';
import { UserIndustry } from '@common/onboarding/userConstants';
import { HUB_URL } from '@common/packageHub/constants';
import { SapAiTermsDialogProvider } from '@common/SapAiTerms/sapAiTermsDialogContext';
import { getSqlIDEUrl, Private_Routes, Public_Routes, VAPP_Private_Routes } from '@common/url';
import { replaceBasePath } from '@common/urlBasePath';
import { InactiveUserDialog, RejectedUserDialog } from '@common/UserStatus';
import { isDeploymentUXEnabled } from '@common/utils/hooks/flags';
import { useIsVappImportProjectEnabled } from '@common/VerticalApps/hooks';
import { useProductConfig } from '@common/VerticalApps/useProductConfig';
import { useIsFinanceVerticalApp, useIsVerticalApp } from '@common/VerticalApps/utils';
import { VerticalAppProvider } from '@common/VerticalApps/VerticalAppContext';
import { MaintenanceUpgradeStatusList } from '@pages/Maintenance/constants';
import { MaintenanceNotification } from '@pages/Maintenance/MaintenanceNotification';
import { ServiceDownNotification } from '@pages/Maintenance/ServiceDownNotification';
import { useDownServiceStatus } from '@pages/Maintenance/util';
import { prefetchSqlPage } from '@pages/Sql/prefetch';
import { InvitationStatus } from '@prophecy/graphql-types/enums';
import { FolderOpenIcon, MetadataIcon, PlusIcon, ProjectIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Stack, TypographyGlobalStyle } from '@prophecy/ui-v3';
import { history, historyReplace, setBasePath, useLocationPathName } from '@prophecy/utils/history';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { withSentryReactRouterV6Routing } from '@sentry/react';
import { isEmpty } from 'lodash-es';
import { lazy, Suspense, useEffect } from 'react';
import {
  matchPath,
  Navigate,
  Outlet,
  Route,
  Routes,
  unstable_HistoryRouter as HistoryRouter,
  useLocation,
  useParams
} from 'react-router-dom';

import {
  AppListPage,
  ComponentListPage,
  CreateEntityPage,
  CreateFabricPage,
  CreateProject,
  CreateTeam,
  DatasetDetailPage,
  DeploymentPage,
  DiscourseOauth,
  DiscourseSSO,
  GemBuilderPage,
  HomePage,
  IdeHome,
  IDEPage,
  JobDetailPage,
  LineagePage,
  LineageSearchPage,
  Maintenance,
  DataboxPage,
  NewObservationHome,
  OAuth2Consent,
  OAuthCallback,
  OAuthSamlCallback,
  Onboarding,
  OnboardingHome,
  PackageDetail,
  PackageHubListPage,
  PipelineDetailPage,
  PrivateMainSection,
  ProjectDetailPage,
  ServiceDown,
  SqlPage,
  SubgraphDetailPage,
  TeamDetailPage,
  TranspilerDetailPage,
  TranspilerImportPage,
  TranspilerListingPage,
  UpdateFabricPage,
  UpgradePlanPage,
  UserDetailPage,
  UserSettings,
  VerticalAppUserSettings,
  VAppCreateProjectPage,
  VAppProjectListingPage,
  JPMCPostLoginCopilotPopup,
  SapAiTermsPrompt
} from '../App';
import { useAppMetadata } from '../context/appMetadata';
import { useGlobalVariables } from '../context/globalConfig/securedConfigHook';
import { useUnsecuredGlobalVariables } from '../context/globalConfig/unsecuredConfigHook';
import { useTabFocusEvents } from '../context/hooks/useTabFocusEvents';
import { UserFabricStatusKnownStatus, UserState } from '../context/types';
import { useUser } from '../context/user';
import { SideNavBar } from '../layout/SideNavBar';
import { useDelightSurvey } from '../trackers/customerDelight';
import { prefetchWhenIdle } from '../utils/prefetchWhenIdle';
import useWebsocketLogs from '../utils/useWebsocketDebug';
import { getPageTitle, NoMatch, StyledRoot } from './common';

const AcceptCookieBanner = lazy(() => import('@atoms/AcceptCookie').then((m) => ({ default: m.AcceptCookieBanner })));
const CreditUsageNotification = lazy(() =>
  import('@atoms/CreditUsageNotification').then((m) => ({ default: m.CreditUsageNotification }))
);
const MonitoringNotification = lazy(() =>
  import('@atoms/Monitoring/MonitoringNotification').then((m) => ({ default: m.MonitoringNotification }))
);
const SnowflakePwdAuthDialog = lazy(() =>
  import('@common/Fabric/SnowflakePwdAuthBanner/SnowflakePwdAuthDialog').then((m) => ({
    default: m.SnowflakePwdAuthDialog
  }))
);

const RouteTemplate = () => {
  const { user } = useUser();

  const sqlFabricStatus = user.sqlFabricStatus;
  const preLoginData = useAppMetadata();
  const globalConfigManager = useGlobalVariables();
  const unsecureGlobalConfigManager = useUnsecuredGlobalVariables();
  const updateStatus = preLoginData?.maintenanceUpdateInfo?.upgrade_status?.status;
  const userInfo = preLoginData?.user;
  const isUnderMaintenance = updateStatus && MaintenanceUpgradeStatusList.indexOf(updateStatus) !== -1;
  const sparkFabricStatus = userInfo?.fabric;
  const isFabricSet = sparkFabricStatus === UserFabricStatusKnownStatus.AlreadyHaveFabric;
  const trialInfo = userInfo?.trialInfo;
  const isCopilotEnabled = useIsCopilotEnabled();
  let isOnboardingRequired = false;
  let onboardingUrl = Private_Routes.Onboarding.getUrl();
  const sparkOnboardingStatus = globalConfigManager.getSparkOnboardingStatus;
  const sqlOnboardingStatus = globalConfigManager.getSqlOnboardingStatus;
  const sparkOnboardingPipelineId = userInfo?.onboardingDetail?.project?.pipelines[0]?._id;
  const sqlOnboardingProjectId = userInfo?.onboardingDetail?.sqlProject?._id;

  const hasOnlyAnalystFabric = useHasOnlyAnalystFabric();

  const sparkOnboarding = sparkOnboardingPipelineId
    ? getSparkOnboardingFlow(
        sparkFabricStatus as number,
        sparkOnboardingStatus,
        sparkOnboardingPipelineId,
        hasOnlyAnalystFabric
      )
    : null;
  const sqlOnboarding = sqlOnboardingProjectId
    ? getSqlOnboardingFlow(sqlFabricStatus, sqlOnboardingStatus, sqlOnboardingProjectId)
    : null;
  const isSqlOnboardingPending = sqlOnboarding && sqlOnboarding.flow === OnboardingStatus.ONBOARDING_PIPELINE;
  const isSparkOnboardingPending = sparkOnboarding && sparkOnboarding.flow === OnboardingStatus.ONBOARDING_PIPELINE;

  const isCreditsEnabled = globalConfigManager.isCreditsEnabled;
  const isOnboardingV2Enabled = globalConfigManager.isOnboardingV2Enabled;
  const isVerticalApp = useIsVerticalApp();
  const userOnboardingDetails = globalConfigManager.getUserOnboardingDetails;
  const userProfession = userOnboardingDetails?.profession || userInfo?.userOnboardingDetails?.profession;
  const userIndustry = userOnboardingDetails?.industry || userInfo?.userOnboardingDetails?.industry;

  const analystFabric = userInfo?.onboardingDetail?.analystFabric;

  // If analystFabric is present, and profession is empty and user has not skipped onboarding v2, then we consider onboarding v2 as required
  const isOnboardingV2Required = Boolean(analystFabric) && isEmpty(userProfession);
  if (isOnboardingV2Enabled && !isVerticalApp) {
    const analystProjectId = userInfo?.onboardingDetail?.analystProject?._id || globalConfigManager.getAnalystProjectId;

    if (analystProjectId) {
      onboardingUrl = getSqlIDEUrl({
        uid: analystProjectId
      });
    } else if (userIndustry === UserIndustry.HOSPITALITY || userIndustry === UserIndustry.OTHER) {
      onboardingUrl = HUB_URL.TEMPLATE;
    } else {
      onboardingUrl = Private_Routes.Onboarding.getUrl();
    }
  } else if (!isVerticalApp) {
    if (isCopilotEnabled) {
      if (isSparkOnboardingPending && isSqlOnboardingPending) {
        isOnboardingRequired = true;
      }
    } else {
      if (isSparkOnboardingPending && sparkOnboarding) {
        isOnboardingRequired = true;
        onboardingUrl = sparkOnboarding.url;
      }
    }
  }
  const enterpriseTrial = Boolean(trialInfo && !trialInfo?.billingStarted);
  const expiryTime: number = trialInfo?.daysLeft || 2;
  const isPlanExpired = Boolean(unsecureGlobalConfigManager.isMeteringEnabled && enterpriseTrial && expiryTime < 1);

  globalConfigManager.setFabricStatus(isFabricSet);
  globalConfigManager.setUnsetFabricStatusId(sparkFabricStatus as number);

  const patterns = [
    '/create/*',
    '/entity/*',
    '/settings/*',
    '/lineage/*',
    '/deployment/*',
    '/pipeline/*',
    '/job/*',
    '/components/*'
  ];

  const pathname = useLocationPathName();
  const routeMatched = patterns.some((pattern) => {
    const match = matchPath(pattern, pathname);
    if (match) {
      return true;
    }
    return false;
  });

  const url = new URL(window.location.href);
  const isRemix = url.searchParams.get('remix') === 'true';

  const isProphecyDown = useDownServiceStatus()?.isProphecyDown;
  useTabFocusEvents();

  let OutletComponent = Outlet;

  //Maintenance page should get the priority than prophecydown
  if (isUnderMaintenance && (pathname !== Public_Routes.Maintenance.getUrl() || routeMatched)) {
    OutletComponent = () => <Navigate replace to={Public_Routes.Maintenance.getUrl()} />;
  } else if (
    isProphecyDown &&
    !isUnderMaintenance &&
    (pathname !== Public_Routes.ProphecyDown.getUrl() || routeMatched)
  ) {
    OutletComponent = () => <Navigate replace to={Public_Routes.ProphecyDown.getUrl()} />;
  } else if (
    isPlanExpired &&
    (pathname === Private_Routes.Home.getUrlWithBasePath() || pathname === Private_Routes.Home.getUrl() || routeMatched)
  ) {
    OutletComponent = () => <Navigate replace to={Private_Routes.UPGRADE_PLAN.getUrl()} />;
  } else if (
    !isUnderMaintenance &&
    (pathname === Public_Routes.Maintenance.getUrl() || pathname === Public_Routes.Maintenance.getUrlWithBasePath())
  ) {
    OutletComponent = () => <Navigate replace to={Private_Routes.Home.getUrl()} />;
  } else if (
    !isRemix &&
    ((isOnboardingRequired && onboardingUrl) ||
      (isOnboardingV2Required &&
        ![
          onboardingUrl,
          sparkOnboarding?.url || '',
          sqlOnboarding?.url.split('?')[0],
          Private_Routes.Settings.home.getUrl()
        ].some((item) => pathname.startsWith(item as string)) &&
        !isVerticalApp))
  ) {
    OutletComponent = () => <Navigate replace to={onboardingUrl} />;
  }

  return (
    <>
      <DocumentTitle title={getPageTitle()} />
      {!isUnderMaintenance && (
        <>
          <ServiceDownNotification />
          <MaintenanceNotification />
          <Suspense fallback={null}>
            <MonitoringNotification />
          </Suspense>
          {isCreditsEnabled && (
            <Suspense fallback={null}>
              <CreditUsageNotification />
            </Suspense>
          )}
          {!isProphecyDown && (
            <Suspense fallback={null}>
              <SnowflakePwdAuthDialog />
            </Suspense>
          )}
        </>
      )}
      <StyledRoot>
        <ProfessionalEditionCreditsProvider>
          <Stack width='100%' direction='horizontal'>
            {!isProphecyDown && !isUnderMaintenance && (
              <SideNavBar isOnboardingRequired={isOnboardingRequired && !isVerticalApp} isPlanExpired={isPlanExpired} />
            )}
            <OutletComponent />
          </Stack>
        </ProfessionalEditionCreditsProvider>
      </StyledRoot>
    </>
  );
};

const SentryRoutes = withSentryReactRouterV6Routing(Routes);

// Session-wide prefetch: not tied to one imminent action, so it can afford a longer deadline than
// ProjectDetail's per-page prefetch (see prefetchWhenIdle's timeoutMs).
const SQL_PREFETCH_IDLE_TIMEOUT_MS = 5000;

function VerticalAppSqlPageRoute() {
  const { uid } = useParams();
  return <SqlPage key={uid} />;
}

function UserSettingsWrapper() {
  return (
    <PrivateMainSection wrapInLayout={true}>
      <UserSettings />
    </PrivateMainSection>
  );
}

function RedirectToVerticalAppProject() {
  const { uid } = useParams();

  return <Navigate to={VAPP_Private_Routes.Project_Detail.getUrl({ uid: uid as string }) + window.location.search} />;
}

function NoMatchOrHome() {
  const { state } = useLocation();
  const rebased = Boolean((state as { rebasedFrom?: string } | null)?.rebasedFrom);

  return rebased ? <Navigate replace to={Private_Routes.Home.getUrl()} /> : <NoMatch />;
}

// Alteryx imports are not part of Structured Finance (available in regular Prophecy and the
// Professional vertical app), so block direct URLs to the transpiler pages there — and on any plan
// without the import entitlement, which also covers switching workspace with an import route open.
export const TranspilerRouteGuard = ({ children }: { children: React.ReactNode }) => {
  const isFinanceVerticalApp = useIsFinanceVerticalApp();
  const isImportBlockedByPlan = useIsImportBlockedByPlan();

  if (isFinanceVerticalApp || isImportBlockedByPlan) {
    return <Navigate replace to={VAPP_Private_Routes.Home.getUrl()} />;
  }
  return <>{children}</>;
};

export const getEnterpriseAppRoutes = ({
  user,
  globalConfigManager,
  shouldRedirectToOnboardingV2
}: {
  user: ReturnType<typeof useUser>;
  globalConfigManager: ReturnType<typeof useGlobalVariables>;
  shouldRedirectToOnboardingV2: boolean;
}) => {
  return (
    <>
      <Route
        key={Private_Routes.Home.url}
        path={Private_Routes.Home.url}
        element={
          <PrivateMainSection>
            <StyledDowngradedToStarterBanner />
            <HomePage />
          </PrivateMainSection>
        }
      />

      <Route
        path={Private_Routes.Create_Entity.home.url}
        key={`CreateEntityHome`}
        element={
          <PrivateMainSection
            wrapInLayout={true}
            banner={<SnowflakePwdAuthBanner />}
            header={
              <PageHeader crumbs={[{ label: 'Create Entity', icon: <PlusIcon type='default' /> }]} noWrap={true} />
            }>
            <CreateEntityPage />
          </PrivateMainSection>
        }
      />

      <Route
        path={Private_Routes.Create_Entity.project.url}
        key={`CreateProject`}
        element={
          <PrivateMainSection>
            <CreateProject />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Create_Entity.fabric.new.url}
        key={`CreateFabric`}
        element={
          <PrivateMainSection>
            <CreateFabricPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Create_Entity.fabric.existing.url}
        key={`CreateFabric`}
        element={
          <PrivateMainSection>
            <CreateFabricPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Create_Entity.tab.url}
        key={`CreateEntityTab`}
        element={
          <PrivateMainSection wrapInLayout={true}>
            <CreateEntityPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Create_Entity.team.url}
        key={`CreateTeam`}
        element={
          <PrivateMainSection>
            <CreateTeam />
          </PrivateMainSection>
        }
      />
      {globalConfigManager.isPlaygroundEnabled && (
        <>
          <Route
            path={Private_Routes.Gems.url}
            key={Private_Routes.Gems.url}
            element={
              <PrivateMainSection wrapInLayout={true}>
                <ComponentListPage />
              </PrivateMainSection>
            }
          />
          <Route
            path={Private_Routes.GemPlayground.url}
            key={Private_Routes.GemPlayground.url}
            element={
              <PrivateMainSection>
                <GemBuilderPage isAirflow={false} />
              </PrivateMainSection>
            }
          />
          <Route
            path={Private_Routes.AirflowGemPlayground.url}
            key={Private_Routes.AirflowGemPlayground.url}
            element={
              <PrivateMainSection>
                <GemBuilderPage isAirflow />
              </PrivateMainSection>
            }
          />
        </>
      )}
      <Route
        path={Private_Routes.UPGRADE_PLAN.url}
        key={Private_Routes.UPGRADE_PLAN.url}
        element={
          <PrivateMainSection wrapInLayout={true}>
            <UpgradePlanPage />
          </PrivateMainSection>
        }
      />

      <Route
        path={Private_Routes.User.home.url}
        key={Private_Routes.User.home.url}
        element={
          <PrivateMainSection
            wrapInLayout={true}
            banner={<SnowflakePwdAuthBanner />}
            header={
              <PageHeader crumbs={[{ label: 'Metadata', icon: <MetadataIcon type='extended' /> }]} noWrap={true} />
            }>
            <UserDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.User.tab.url}
        key={Private_Routes.User.tab.url}
        element={
          <PrivateMainSection
            wrapInLayout={true}
            banner={<SnowflakePwdAuthBanner />}
            header={
              <PageHeader crumbs={[{ label: 'Metadata', icon: <MetadataIcon type='extended' /> }]} noWrap={true} />
            }>
            <UserDetailPage />
          </PrivateMainSection>
        }
      />

      <Route
        path={Private_Routes.Lineage_IDE.datasetOrPipeline.url}
        key={`datasetOrPipeline_lineage`}
        element={
          <PrivateMainSection minBreakpoint={theme.breakpoints.l}>
            <LineagePage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Lineage_IDE.column.url}
        key={`column_lineage`}
        element={
          <PrivateMainSection minBreakpoint={theme.breakpoints.l}>
            <LineagePage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Lineage_Search.url}
        key={Private_Routes.Lineage_Search.url}
        element={
          <PrivateMainSection>
            <LineageSearchPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Team.home.url}
        key={Private_Routes.Team.home.url}
        element={
          <PrivateMainSection>
            <TeamDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Team.tab.url}
        key={Private_Routes.Team.tab.url}
        element={
          <PrivateMainSection>
            <TeamDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Subgraph.home.url}
        key={Private_Routes.Subgraph.home.url}
        element={
          <PrivateMainSection>
            <SubgraphDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Subgraph.tab.url}
        key={Private_Routes.Subgraph.tab.url}
        element={
          <PrivateMainSection>
            <SubgraphDetailPage />
          </PrivateMainSection>
        }
      />

      <Route
        path={Private_Routes.Dataset.home.url}
        key={Private_Routes.Dataset.home.url}
        element={
          <PrivateMainSection>
            <DatasetDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Dataset.tab.url}
        key={Private_Routes.Dataset.tab.url}
        element={
          <PrivateMainSection>
            <DatasetDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Job.home.url}
        key={Private_Routes.Job.home.url}
        element={
          <PrivateMainSection>
            <JobDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Job.tab.url}
        key={Private_Routes.Job.tab.url}
        element={
          <PrivateMainSection>
            <JobDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Pipeline.home.url}
        key={Private_Routes.Pipeline.home.url}
        element={
          <PrivateMainSection>
            <PipelineDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Pipeline.tab.url}
        key={Private_Routes.Pipeline.tab.url}
        element={
          <PrivateMainSection>
            <PipelineDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Settings.home.url}
        key={Private_Routes.Settings.home.url}
        element={<UserSettingsWrapper />}
      />
      <Route
        path={Private_Routes.Settings.tab.url}
        key={Private_Routes.Settings.tab.url}
        element={<UserSettingsWrapper />}
      />
      <Route
        path={Private_Routes.Project.home.url}
        key={Private_Routes.Project.home.url}
        element={
          <PrivateMainSection>
            <ProjectDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Project.tab.url}
        key={Private_Routes.Project.tab.url}
        element={
          <PrivateMainSection>
            <ProjectDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Fabric.home.url}
        key={Private_Routes.Fabric.home.url}
        element={
          <PrivateMainSection>
            <UpdateFabricPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Fabric.tab.url}
        key={Private_Routes.Fabric.tab.url}
        element={
          <PrivateMainSection>
            <UpdateFabricPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.Transpiler.url}
        key={Private_Routes.Transpiler.url}
        element={
          <PrivateMainSection>
            <TranspilerListingPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.TranspilerDetail.url}
        key={Private_Routes.TranspilerDetail.url}
        element={
          <PrivateMainSection>
            <TranspilerDetailPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.TranspilerImport.home.url}
        key={Private_Routes.TranspilerImport.home.url}
        element={
          <PrivateMainSection>
            <TranspilerImportPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.TranspilerImport.tab.url}
        key={Private_Routes.TranspilerImport.tab.url}
        element={
          <PrivateMainSection>
            <TranspilerImportPage />
          </PrivateMainSection>
        }
      />
      <Route
        path={Private_Routes.IDE_Home.url}
        key={Private_Routes.IDE_Home.url}
        element={
          <PrivateMainSection minBreakpoint={theme.breakpoints.l}>
            <IdeHome />
          </PrivateMainSection>
        }
      />
      <Route
        key={Private_Routes.IDE.url}
        path={Private_Routes.IDE.url}
        element={
          <PrivateMainSection minBreakpoint={theme.breakpoints.l}>
            <IDEPage />
          </PrivateMainSection>
        }
      />

      {globalConfigManager.isSQLProjectEnabled && (
        <Route
          key={Private_Routes.SQL_IDE.url}
          path={Private_Routes.SQL_IDE.url}
          element={
            <PrivateMainSection minBreakpoint={theme.breakpoints.l}>
              <SqlPage />
            </PrivateMainSection>
          }
        />
      )}

      <Route
        key={Private_Routes.Onboarding.url}
        path={Private_Routes.Onboarding.url}
        element={
          <PrivateMainSection wrapInLayout={true}>
            {shouldRedirectToOnboardingV2 ? <Onboarding /> : <OnboardingHome />}
          </PrivateMainSection>
        }
      />
      <Route
        key={Private_Routes.Apps.home.url}
        path={Private_Routes.Apps.home.url}
        element={
          <PrivateMainSection>
            <AppListPage />
          </PrivateMainSection>
        }
      />
      {isDeploymentUXEnabled(user.user) && (
        <Route
          key={Private_Routes.Deployment.url}
          path={Private_Routes.Deployment.url}
          element={
            <PrivateMainSection>
              <DeploymentPage />
            </PrivateMainSection>
          }
        />
      )}
    </>
  );
};

const getVerticalAppRoutes = (isVappImportProjectEnabled: boolean) => {
  const VAPP_CREATE_PROJECT_HEADER = (
    <PageHeader
      crumbs={[
        {
          label: 'Projects',
          url: VAPP_Private_Routes.Project_Listing.getUrl(),
          icon: <FolderOpenIcon type='extended' size='xs' color={theme.colors.gray500} />
        },
        {
          label: 'Create Project',
          icon: <ProjectIcon type='extended' size='xs' color={theme.colors.gray500} />
        }
      ]}
      noWrap={true}
    />
  );

  return (
    <>
      <Route
        key={VAPP_Private_Routes.Project_Listing.url}
        path={VAPP_Private_Routes.Project_Listing.url}
        element={
          <PrivateMainSection>
            <VAppProjectListingPage />
          </PrivateMainSection>
        }
      />

      <Route
        key={VAPP_Private_Routes.Project_Create.url}
        path={VAPP_Private_Routes.Project_Create.url}
        element={
          <PrivateMainSection
            wrapInLayout={true}
            headerEffects={false}
            background={theme.colors.gray25}
            header={VAPP_CREATE_PROJECT_HEADER}>
            <VAppCreateProjectPage />
          </PrivateMainSection>
        }
      />

      <Route
        key={VAPP_Private_Routes.Project_Detail.url}
        path={VAPP_Private_Routes.Project_Detail.url}
        element={
          <PrivateMainSection minBreakpoint={theme.breakpoints.l}>
            <VerticalAppSqlPageRoute />
          </PrivateMainSection>
        }
      />

      {/** Redirect all old sql ide url to new project url*/}
      {!isVappImportProjectEnabled && (
        <Route path={Private_Routes.SQL_IDE.url} element={<RedirectToVerticalAppProject />} />
      )}

      <Route
        path={VAPP_Private_Routes.Monitoring.url}
        element={
          <PrivateMainSection>
            <NewObservationHome />
          </PrivateMainSection>
        }
      />

      <Route
        key={VAPP_Private_Routes.Databox.url}
        path={VAPP_Private_Routes.Databox.url}
        element={
          <PrivateMainSection>
            <DataboxPage />
          </PrivateMainSection>
        }
      />

      <Route
        path={Private_Routes.Settings.home.url}
        key='VAppSettingsHome'
        element={
          <PrivateMainSection>
            <VerticalAppUserSettings />
          </PrivateMainSection>
        }
      />

      <Route
        path={Private_Routes.Settings.tab.url}
        key='VAppSettingsTab'
        element={
          <PrivateMainSection>
            <VerticalAppUserSettings />
          </PrivateMainSection>
        }
      />

      {isVappImportProjectEnabled && (
        <Route
          key={Private_Routes.SQL_IDE.url}
          path={Private_Routes.SQL_IDE.url}
          element={
            <PrivateMainSection minBreakpoint={theme.breakpoints.l}>
              <SqlPage />
            </PrivateMainSection>
          }
        />
      )}

      <Route
        path={Private_Routes.Transpiler.url}
        key={`vapp-${Private_Routes.Transpiler.url}`}
        element={
          <TranspilerRouteGuard>
            <PrivateMainSection>
              <TranspilerListingPage />
            </PrivateMainSection>
          </TranspilerRouteGuard>
        }
      />
      <Route
        path={Private_Routes.TranspilerDetail.url}
        key={`vapp-${Private_Routes.TranspilerDetail.url}`}
        element={
          <TranspilerRouteGuard>
            <PrivateMainSection>
              <TranspilerDetailPage />
            </PrivateMainSection>
          </TranspilerRouteGuard>
        }
      />
      <Route
        path={Private_Routes.TranspilerImport.home.url}
        key={`vapp-${Private_Routes.TranspilerImport.home.url}`}
        element={
          <TranspilerRouteGuard>
            <PrivateMainSection>
              <TranspilerImportPage />
            </PrivateMainSection>
          </TranspilerRouteGuard>
        }
      />
      <Route
        path={Private_Routes.TranspilerImport.tab.url}
        key={`vapp-${Private_Routes.TranspilerImport.tab.url}`}
        element={
          <TranspilerRouteGuard>
            <PrivateMainSection>
              <TranspilerImportPage />
            </PrivateMainSection>
          </TranspilerRouteGuard>
        }
      />
    </>
  );
};

const PrivateRoutes = function () {
  const globalConfigManager = useGlobalVariables();
  const { user: userData } = useAppMetadata();
  const user = useUser();
  const { analystFabric } = useOnboardingDetail();
  const isVappImportProjectEnabled = useIsVappImportProjectEnabled();

  useDelightSurvey();
  useWebsocketLogs();

  const checkUserState = globalConfigManager.isCreditsEnabled;
  const isInActiveUser = checkUserState && userData?.userState === UserState.inactive;
  const isRejectedUser =
    checkUserState &&
    (userData?.userState === UserState.rejected ||
      [InvitationStatus.rejected, InvitationStatus.suspended].includes(userData?.invitationStatus as InvitationStatus));

  const defaultRoute = (
    <Route
      path='*'
      element={isInActiveUser ? <InactiveUserDialog /> : isRejectedUser ? <RejectedUserDialog /> : <NoMatchOrHome />}
    />
  );

  const productConfig = useProductConfig();

  // // if route is "/", replace the url to /finance or /professional or /metadata
  useEffect(() => {
    setBasePath(productConfig.basePath);

    if (window.location.pathname === '/') {
      // since basepath is set above, just replace the url to /
      historyReplace('/');
    } else if (productConfig.basePath && !window.location.pathname.startsWith(productConfig.basePath)) {
      replaceBasePath(productConfig.basePath);
    }
  }, [productConfig.basePath]);

  const isOnboardingV2Enabled = globalConfigManager.isOnboardingV2Enabled;
  const shouldRedirectToOnboardingV2 = Boolean(isOnboardingV2Enabled && analystFabric);

  const isVerticalApp = useIsVerticalApp();

  // Vertical apps are SQL-only, so any vertical-app session will open the SQL IDE — prefetch its
  // chunk once idle, covering every entry point (project listing, recent activity, home, side-nav)
  // instead of duplicating the prefetch per entry point. Idle-deferred so it doesn't compete with
  // whatever the user's actual landing route needs to fetch/render right now.
  useEffect(() => {
    // Inactive/rejected sessions only ever render defaultRoute (see below) — the vertical-app
    // routes never register, so prefetching SQL for them would just be a wasted download.
    if (!isVerticalApp || isInActiveUser || isRejectedUser) return;
    return prefetchWhenIdle(prefetchSqlPage, SQL_PREFETCH_IDLE_TIMEOUT_MS);
  }, [isVerticalApp, isInActiveUser, isRejectedUser]);

  return (
    <VerticalAppProvider isVerticalApp={isVerticalApp}>
      <SapAiTermsDialogProvider>
        <TypographyGlobalStyle />
        <Suspense fallback={null}>
          <AcceptCookieBanner />
        </Suspense>
        {!isVerticalApp && globalConfigManager.showJpmcPostLoginCopilotPopup && (
          <Suspense fallback={null}>
            <JPMCPostLoginCopilotPopup />
          </Suspense>
        )}
        {!isVerticalApp && globalConfigManager.isSapAiScrollWrapEnabled && (
          <Suspense fallback={null}>
            <SapAiTermsPrompt />
          </Suspense>
        )}
        <HistoryRouter basename={productConfig.basePath} history={history}>
          <SentryRoutes>
            <Route element={<RouteTemplate />}>
              <Route
                key={Public_Routes.OauthCallback.url}
                path={Public_Routes.OauthCallback.url}
                element={
                  <PrivateMainSection>
                    <OAuthCallback />
                  </PrivateMainSection>
                }
              />

              <Route
                key={Public_Routes.OauthSamlCallback.url}
                path={Public_Routes.OauthSamlCallback.url}
                element={
                  <PrivateMainSection>
                    <OAuthSamlCallback />
                  </PrivateMainSection>
                }
              />

              <Route
                key={Private_Routes.DiscourseSSO.url}
                path={Private_Routes.DiscourseSSO.url}
                element={
                  <PrivateMainSection>
                    <DiscourseSSO />
                  </PrivateMainSection>
                }
              />

              <Route
                key={Private_Routes.DiscourseOauth.url}
                path={Private_Routes.DiscourseOauth.url}
                element={
                  <PrivateMainSection>
                    <DiscourseOauth />
                  </PrivateMainSection>
                }
              />
              <Route
                key={Public_Routes.Maintenance.url}
                path={Public_Routes.Maintenance.url}
                element={
                  <PrivateMainSection wrapInLayout={true}>
                    <Maintenance />
                  </PrivateMainSection>
                }
              />
              <Route
                key={Public_Routes.ProphecyDown.url}
                path={Public_Routes.ProphecyDown.url}
                element={
                  <PrivateMainSection wrapInLayout={true}>
                    <ServiceDown />
                  </PrivateMainSection>
                }
              />

              {!isInActiveUser && !isRejectedUser && (
                <>
                  <Route
                    key={Private_Routes.Home.url}
                    path={Private_Routes.Home.url}
                    element={
                      <PrivateMainSection>
                        <StyledDowngradedToStarterBanner />
                        <HomePage />
                      </PrivateMainSection>
                    }
                  />
                  <Route
                    key={VAPP_Private_Routes.Home.url}
                    path={VAPP_Private_Routes.Home.url}
                    element={
                      <PrivateMainSection>
                        <StyledDowngradedToStarterBanner />
                        <HomePage />
                      </PrivateMainSection>
                    }
                  />
                  <Route
                    path={Private_Routes.Package.home.url}
                    key={Private_Routes.Package.home.url}
                    element={
                      <PrivateMainSection>
                        <PackageDetail />
                      </PrivateMainSection>
                    }
                  />
                  <Route
                    path={Private_Routes.Package.tab.url}
                    key={Private_Routes.Package.tab.url}
                    element={
                      <PrivateMainSection>
                        <PackageDetail />
                      </PrivateMainSection>
                    }
                  />
                  <Route
                    path={Private_Routes.PackageHubList.url}
                    key={Private_Routes.PackageHubList.url}
                    element={
                      <PrivateMainSection>
                        <PackageHubListPage />
                      </PrivateMainSection>
                    }
                  />

                  <>
                    {isVerticalApp
                      ? getVerticalAppRoutes(isVappImportProjectEnabled)
                      : getEnterpriseAppRoutes({
                          user,
                          globalConfigManager,
                          shouldRedirectToOnboardingV2
                        })}
                  </>
                </>
              )}

              {defaultRoute}
            </Route>

            <Route
              key={Public_Routes.OauthConsent.url}
              path={Public_Routes.OauthConsent.url}
              element={
                <Suspense fallback={null}>
                  <OAuth2Consent />
                </Suspense>
              }
            />

            {defaultRoute}
          </SentryRoutes>
        </HistoryRouter>
      </SapAiTermsDialogProvider>
    </VerticalAppProvider>
  );
};

export default PrivateRoutes;
